import { useState } from "react";
import {
  AdminActionCancelled,
  clearTestData,
  cloneProductionData,
  loadDemoData,
  publishProduction,
} from "@/lib/adminDevActions";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { devToolsVisibility } from "@/pages/stammdaten/settings/devToolsVisibility";
import { errorMessage } from "@/pages/stammdaten/settings/tenantSettingsTypes";

function Feedback({ success, error }: { success: string | null; error: string | null }) {
  return (
    <>
      {success ? <p className="text-sm text-status-green">{success}</p> : null}
      {error ? <p className="text-sm text-status-red">{error}</p> : null}
    </>
  );
}

export function DevToolsSection({ canWrite }: { canWrite: boolean }) {
  const visibility = devToolsVisibility();

  const [demoSeedToday, setDemoSeedToday] = useState("");
  const [demoSeedReset, setDemoSeedReset] = useState(false);
  const [demoSeedLoading, setDemoSeedLoading] = useState(false);
  const [demoSeedSuccess, setDemoSeedSuccess] = useState<string | null>(null);
  const [demoSeedError, setDemoSeedError] = useState<string | null>(null);
  const [clearTestDataLoading, setClearTestDataLoading] = useState(false);
  const [clearTestDataSuccess, setClearTestDataSuccess] = useState<string | null>(null);
  const [clearTestDataError, setClearTestDataError] = useState<string | null>(null);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [cloneSuccess, setCloneSuccess] = useState<string | null>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);

  async function onPublishProduction() {
    if (!canWrite) return;
    setPublishLoading(true);
    setPublishSuccess(null);
    setPublishError(null);
    try {
      setPublishSuccess(await publishProduction());
    } catch (err) {
      if (!(err instanceof AdminActionCancelled)) {
        setPublishError(errorMessage(err, "Veröffentlichung konnte nicht gestartet werden"));
      }
    } finally {
      setPublishLoading(false);
    }
  }

  async function onCloneProductionData() {
    if (!canWrite) return;
    setCloneLoading(true);
    setCloneSuccess(null);
    setCloneError(null);
    try {
      setCloneSuccess(await cloneProductionData());
    } catch (err) {
      if (!(err instanceof AdminActionCancelled)) {
        setCloneError(errorMessage(err, "Produktionsdaten konnten nicht geladen werden"));
      }
    } finally {
      setCloneLoading(false);
    }
  }

  async function onClearTestData() {
    if (!canWrite) return;
    setClearTestDataLoading(true);
    setClearTestDataSuccess(null);
    setClearTestDataError(null);
    try {
      setClearTestDataSuccess(await clearTestData());
    } catch (err) {
      if (!(err instanceof AdminActionCancelled)) {
        setClearTestDataError(errorMessage(err, "Testdaten konnten nicht gelöscht werden"));
      }
    } finally {
      setClearTestDataLoading(false);
    }
  }

  async function onLoadDemoData() {
    if (!canWrite) return;
    setDemoSeedLoading(true);
    setDemoSeedSuccess(null);
    setDemoSeedError(null);
    try {
      setDemoSeedSuccess(
        await loadDemoData({
          reset: demoSeedReset,
          today: demoSeedToday.trim() || undefined,
          confirm: false,
        }),
      );
    } catch (err) {
      if (!(err instanceof AdminActionCancelled)) {
        setDemoSeedError(errorMessage(err, "Demodaten konnten nicht geladen werden"));
      }
    } finally {
      setDemoSeedLoading(false);
    }
  }

  if (!visibility.any) {
    return (
      <Card title="Testsystem" bodyClassName="text-sm text-label-secondary">
        <p>Auf diesem System sind keine Entwicklungs- oder Schulungswerkzeuge verfügbar.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {visibility.showPublishProduction ? (
        <Card title="Production veröffentlichen" bodyClassName="space-y-4">
          <p className="text-sm text-label-secondary">
            Überträgt den aktuellen Stand von dev.app auf app.: campdesk-api und campdesk-frontend
            werden von branch dev nach main promotet. Anschließend deployt die GitHub Action die API
            nach /opt/campdesk-api und wendet Datenbank-Migrationen auf campdesk_db an. Das Frontend
            wird über Netlify aus main neu gebaut.
          </p>
          <Feedback success={publishSuccess} error={publishError} />
          {canWrite ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="primary"
                loading={publishLoading}
                onClick={() => void onPublishProduction()}
              >
                Auf Production veröffentlichen
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {visibility.showProductionClone ? (
        <Card title="Produktionsdaten laden (Test)" bodyClassName="space-y-4">
          <p className="text-sm text-label-secondary">
            Kopiert den aktuellen Datenstand aus der Produktionsumgebung (Mandant default) in den
            Schulungs-Mandanten test: Stammdaten, Einstellungen, Gäste, Buchungen, Rechnungen und
            alle weiteren Fachdaten. So lassen sich Produktionsprobleme auf test.app.* nachstellen,
            ohne die Produktionsdaten zu verändern. Vorhandene Testdaten werden dabei ersetzt.
          </p>
          <Feedback success={cloneSuccess} error={cloneError} />
          {canWrite ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="primary"
                loading={cloneLoading}
                onClick={() => void onCloneProductionData()}
              >
                Produktionsdaten laden
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {visibility.showClearTestData ? (
        <Card title="Testdaten löschen (Dev)" bodyClassName="space-y-4">
          <p className="text-sm text-label-secondary">
            Entfernt alle Fachdaten des aktuellen Mandanten restlos: Gäste, Buchungen, Rechnungen,
            Zahlungen, Verträge, Tourist-Importe (inkl. hochgeladener Dateien) und Audit-Protokolle.
            Stammdaten wie Stellplätze, Platzkategorien, Artikel, Konten, Rabatte und Einstellungen
            bleiben unverändert. Die Konfiguration von Gäste-Tablets (Arbeitsplätze, Geräte,
            Inhalte) und Infobildschirmen (Bildschirme, Geräte, Seiten, Veranstaltungen) wird nicht
            gelöscht.
          </p>
          <Feedback success={clearTestDataSuccess} error={clearTestDataError} />
          {canWrite ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="danger"
                loading={clearTestDataLoading}
                onClick={() => void onClearTestData()}
              >
                Alle Testdaten löschen
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {visibility.showDemoSeedTools ? (
        <Card title="Demodaten (Dev)" bodyClassName="space-y-4">
          <p className="text-sm text-label-secondary">
            Lädt einen Beispiel-Campingbetrieb ab 01.04.2026 in die Datenbank des aktuellen
            Mandanten (Gäste, Buchungen, Rechnungen, Zahlungen). Verfügbar auf Dev- und
            Schulungs-Systemen (test.app.*).
          </p>
          <fieldset className="space-y-3" disabled={!canWrite}>
            <label className="flex max-w-xs flex-col gap-1 text-sm">
              Simulations-Stichtag (optional)
              <input
                type="date"
                className="eh-input"
                value={demoSeedToday}
                onChange={(e) => setDemoSeedToday(e.target.value)}
              />
            </label>
            {visibility.showClearTestData ? (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-separator"
                  checked={demoSeedReset}
                  onChange={(e) => setDemoSeedReset(e.target.checked)}
                />
                <span className="text-label">
                  Vorher löschen (bestehende Fachdaten des Mandanten entfernen)
                </span>
              </label>
            ) : null}
          </fieldset>
          <Feedback success={demoSeedSuccess} error={demoSeedError} />
          {canWrite ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="danger"
                loading={demoSeedLoading}
                onClick={() => void onLoadDemoData()}
              >
                Demodaten laden
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
